import { getAIToolStartupTracker } from './aiToolsStartup'

const getVoshiAppsVersion = () => {
    const drawerElement = document.getElementById('chatbot_drawer')
    return drawerElement?.dataset?.voshiAppsVersion || window.__myeducatorVoshiAppsVersion || ''
}

const buildVersionedUrl = url => {
    if (!url) {
        return null
    }

    const version = getVoshiAppsVersion() || window.VLib?.version?.REVISION || window.VLib?.version?.BIRTHDAY || ''
    if (!version) {
        return url
    }

    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}v=${encodeURIComponent(version)}`
}

export const getVoshiAppsUrl = () => {
    const drawerElement = document.getElementById('chatbot_drawer')
    const baseUrl = drawerElement && drawerElement.dataset.voshiAppsUrl
    const resolvedUrl = baseUrl ? baseUrl.replace('{app}', 'aitools').replace('{build}', 's') : null
    return buildVersionedUrl(resolvedUrl)
}

export const reregisterAIToolsComponent = async () => {
    // Remove existing script
    const existingScript = document.querySelector('script[data-component="vapp-aitools"]')
    if (existingScript) {
        existingScript.remove()
    }

    // Reset loading state
    window.__aiToolsLoading = false
    delete window.__aiToolsLoadingPromise

    // Force reload the component script
    return ensureAIToolsDefined()
}

export const ensureAIToolsDefined = async () => {
    const tracker = getAIToolStartupTracker()
    if (window.customElements && window.customElements.get('vapp-aitools')) {
        tracker.markOnce('aitools-loader-ready', { source: 'existing-definition' })
        return true
    }

    if (window.__aiToolsLoading) {
        return window.__aiToolsLoadingPromise
    }

    const scriptUrl = getVoshiAppsUrl()
    if (!scriptUrl) return false

    tracker.markOnce('aitools-loader-start', { scriptUrl })
    window.__aiToolsLoading = true
    window.__aiToolsLoadingPromise = (async () => {
        try {
            const existingScript = document.querySelector('script[data-component="vapp-aitools"]')
            if (existingScript) {
                try {
                    await window.customElements.whenDefined('vapp-aitools')
                    tracker.markOnce('aitools-loader-ready', { source: 'existing-script' })
                    return !!window.customElements.get('vapp-aitools')
                } catch (error) {
                    return false
                }
            }

            const script = document.createElement('script')
            script.type = 'module'
            script.src = scriptUrl
            script.setAttribute('data-component', 'vapp-aitools')

            await new Promise((resolve, reject) => {
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })

            if (window.customElements && window.customElements.whenDefined) {
                await window.customElements.whenDefined('vapp-aitools')
                tracker.markOnce('aitools-loader-ready', { source: 'network-load' })
                return true
            }

            return false
        } finally {
            window.__aiToolsLoading = false
        }
    })()

    return window.__aiToolsLoadingPromise
}

// Enhanced content validation function
export const validateAIToolsContent = (element, tooltype = 'aichat') => {
    if (!element || !element.shadowRoot) return false

    const shadowRoot = element.shadowRoot
    const vappRoot = shadowRoot.querySelector('.vapp-root')
    if (!vappRoot) return false

    // Check for content based on tool type
    let hasContent = false

    if (tooltype === 'aisettings') {
        // For AI settings, check for settings-specific content
        hasContent = vappRoot.children.length > 0 && (
            vappRoot.querySelector('[class*="settings"]') ||
            vappRoot.querySelector('form') ||
            vappRoot.querySelector('input') ||
            vappRoot.querySelector('select') ||
            vappRoot.querySelector('[class*="aiSettings"]') ||
            // Fallback: any content is better than empty
            vappRoot.children.length > 0
        )
    } else if (tooltype === 'quickreview') {
        hasContent = vappRoot.children.length > 0 && (
            vappRoot.querySelector('[class*="qr-"]') ||
            vappRoot.querySelector('button') ||
            vappRoot.querySelector('a') ||
            vappRoot.children.length > 0
        )
    } else if (tooltype === 'aisearch') {
        // AISearchPage intentionally renders null until it receives a query,
        // so its mounted root is the valid-content signal for this host.
        hasContent = true
    } else {
        // Default validation for chat and other tools
        hasContent = vappRoot.children.length > 0 && (
            vappRoot.querySelector('.aiChat') ||
            vappRoot.querySelector('.aiChatWindow') ||
            vappRoot.querySelector('.chatMessages') ||
            vappRoot.querySelector('[class*="chat"]') // Any class containing "chat"
        )
    }

    return hasContent
}

export const createAIToolsElement = async (config, retryCount = 0) => {
    const {
        containerRef,
        elementRef,
        isMountedRef,
        setElementCreated,
        attributes = {},
        onSuccess,
        forceRecreate = false,
        reregisterOnFailure = false
    } = config

    const MAX_RETRIES = 3

    if (!containerRef.current || !isMountedRef.current) return

    const existingElement = containerRef.current.querySelector('vapp-aitools')
    if (existingElement && !forceRecreate) {
        if (setElementCreated) setElementCreated(true)
        return existingElement
    }

    if (existingElement && forceRecreate) {
        existingElement.remove()
    }

    try {
        const isDefined = await ensureAIToolsDefined()
        if (!isDefined || !isMountedRef.current || !containerRef.current) return

        const ElementConstructor = window.customElements.get('vapp-aitools')
        if (!ElementConstructor) return

        const aiToolsElement = new ElementConstructor(attributes)

        Object.assign(aiToolsElement.style, {
            position: 'relative',
            height: '100%',
            width: '100%'
        })

        if (elementRef) elementRef.current = aiToolsElement
        containerRef.current.appendChild(aiToolsElement)

        const validationTimeout = attributes.tooltype === 'aisettings' ? 2000 : 5000
        setTimeout(async () => {
            if (isMountedRef.current && aiToolsElement.isConnected) {
                const tooltype = attributes.tooltype || 'aichat'
                const hasValidContent = validateAIToolsContent(aiToolsElement, tooltype)

                if (hasValidContent) {
                    if (setElementCreated) setElementCreated(true)
                } else {
                    console.warn('AI Tools element created but content validation failed')

                    if (reregisterOnFailure && retryCount < MAX_RETRIES) {
                        aiToolsElement.remove()

                        try {
                            await reregisterAIToolsComponent()
                            setTimeout(() => {
                                createAIToolsElement({
                                    ...config,
                                    forceRecreate: true,
                                    reregisterOnFailure: false
                                }, retryCount + 1)
                            }, 2000)
                        } catch (error) {
                            console.error('Component re-registration failed:', error)
                            if (setElementCreated) setElementCreated(false)
                        }
                    } else {
                        if (setElementCreated) setElementCreated(false)
                    }
                }
            }
        }, validationTimeout)

        if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(aiToolsElement)
        }

        return aiToolsElement

    } catch (error) {
        console.error(`AI tools element creation attempt ${retryCount + 1} failed:`, error)

        if (retryCount < MAX_RETRIES - 1) {
            const backoffTime = 1000 * Math.pow(2, retryCount)
            await new Promise(resolve => setTimeout(resolve, backoffTime))
            return createAIToolsElement(config, retryCount + 1)
        }

        if (containerRef.current && isMountedRef.current && setElementCreated) {
            const errorDiv = document.createElement('div')
            errorDiv.style.cssText = 'padding: 20px; text-align: center;'
            errorDiv.innerHTML = '<p>Unable to load AI tools. Please refresh the page and try again.</p>'
            containerRef.current.appendChild(errorDiv)
            setElementCreated(true)
        }
    }
}
